import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { runQcAnalysis } from 'api/platform';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { JobType, QcAnalysisJob } from 'interfaces/job';
import { indexOf, isEmpty, map } from 'lodash';
import { useSnackbar } from 'notistack';
import { default as React, ReactElement, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import * as yup from 'yup';
import { ActionStepper } from '../../ActionStepper';
import { JobWithRebuild } from '../../JobWithRebuild';
import { LoadFromPreviousJobStep } from '../../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import { JobNameAndDescriptionStep } from '../../utils';
import { MultiplexSelectionAlert } from '../MultiplexSelectionAlert';
import { useMultiplexActionSelection } from '../useMultiplexActionSelection';
import { AdditionalParametersStep } from './AdditionalParametersStep';
import { QcAnalysisFormValues, runQcAnalysisOrder, RunQcAnalysisStep } from './types';

const SNACK_BAR_KEY_RUN_QC_ANALYSIS = 'RUN_QC_ANALYSIS';

export interface RunQcAnalysisProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
}

const defaultValues: QcAnalysisFormValues = {
  jobName: '',
  jobDescription: '',
  configParams: {
    markers: [],
  },
};

const validationSchema = {
  [RunQcAnalysisStep.LoadParams]: yup.object({}),
  [RunQcAnalysisStep.NameAndDescription]: yup.object({}),
  [RunQcAnalysisStep.AdditionalParameters]: yup.object({
    configParams: yup.object().shape({
      markers: yup
        .array()
        .of(yup.string().required('Marker name is required'))
        .min(1, 'At least one marker must be selected')
        .required('Marker selection is required'),
    }),
  }),
};

export const RunQcAnalysis = (props: RunQcAnalysisProps): ReactElement => {
  const { onClose, casesParams, jobId } = props;
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [selectedJobId, setSelectedJobId] = useState<string>(undefined);
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(JobType.QcAnalysis);
  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});

  const currentValidationSchema = validationSchema[runQcAnalysisOrder[activeStep]];

  const methods = useForm<QcAnalysisFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: defaultValues,
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    formState: { errors },
  } = methods;

  const runQcAnalysisMutation = useMutationWithErrorSnackbar({
    mutationFn: runQcAnalysis,
    mutationDescription: 'run QC analysis',
    onSuccess: () => {
      enqueueSnackbar('QC analysis started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_QC_ANALYSIS);
    },
  });

  const checkValidationAndSetIsStepFailed = async (step: RunQcAnalysisStep) => {
    const stepIndex = indexOf(runQcAnalysisOrder, step);
    let isValid = await trigger();
    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };

  const onSelectedJobParamChange = (newValue: QcAnalysisJob) => {
    if (isEmpty(newValue)) {
      setSelectedJobId(undefined);
      reset(defaultValues);
    } else {
      setSelectedJobId(newValue.id);
      reset({
        jobName: newValue?.name,
        jobDescription: newValue?.description,
        configParams: newValue?.params,
      });
    }
  };

  const stepsMap = {
    [RunQcAnalysisStep.LoadParams]: {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={JobType.QcAnalysis}
          onSelectedJob={onSelectedJobParamChange}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    [RunQcAnalysisStep.NameAndDescription]: {
      label: 'Job Name and Description',
      subLabel: activeStep !== indexOf(runQcAnalysisOrder, RunQcAnalysisStep.NameAndDescription) && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },

    [RunQcAnalysisStep.AdditionalParameters]: {
      label: 'Analysis settings',
      content: (
        <AdditionalParametersStep
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(RunQcAnalysisStep.AdditionalParameters)
          }
          casesParams={multiplexCasesParams}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunQcAnalysisStep.AdditionalParameters),
    },
  };

  const steps = map(runQcAnalysisOrder, (stepId) => stepsMap[stepId]);

  const onSubmit: SubmitHandler<any> = (data: QcAnalysisFormValues) => {
    runQcAnalysisMutation.mutate({
      ...multiplexCasesParams,
      ...data,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for QC analysis to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_QC_ANALYSIS,
      autoHideDuration: null,
    });
  };

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <JobWithRebuild jobId={jobId} onSelectedJobParamChange={onSelectedJobParamChange}>
      <Grid container direction="column" spacing={2}>
        {multiplexSelectionInfoMessage ? (
          <Grid item>
            <MultiplexSelectionAlert
              severity="info"
              title="This action runs on slides"
              message={multiplexSelectionInfoMessage}
            />
          </Grid>
        ) : null}
        <Grid item>
          <ActionStepper
            handleSubmit={handleSubmit(onSubmit)}
            steps={steps}
            setActiveStepForValidation={setActiveStep}
            isStepFailed={isStepFailed}
            isInitializing={isCheckingPreviousJobs}
            initializingText="Checking for previous jobs…"
            validateStep={() => checkValidationAndSetIsStepFailed(runQcAnalysisOrder[activeStep])}
          />
        </Grid>
      </Grid>
    </JobWithRebuild>
  );
};
